import { reportApiError } from '@services/error-reporting';
import { useApiLoadingStore } from '@stores/api-loading.store';
import { useAuthStore } from '@stores/auth.store';
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function isMutation(config?: InternalAxiosRequestConfig): boolean {
  return MUTATION_METHODS.has((config?.method ?? '').toLowerCase());
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const SUPER_ADMIN_SYNTHETIC_TENANT_ID = '00000000-0000-0000-0000-000000000000';

const AUTH_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
];

function shouldSkipRefresh(path?: string): boolean {
  if (!path) return false;
  return AUTH_PATHS.some((authPath) => path.includes(authPath));
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});


// Decode JWT exp without a library — returns epoch seconds or null
function isIpv4Host(hostname: string): boolean {
  return /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
}

function isReservedWorkspaceLabel(label: string): boolean {
  return ['localhost', 'www', 'api', 'app'].includes(label);
}

function resolveTenantHeader(user?: { tenantId?: string; role?: string | null } | null): string | undefined {
  if (user?.tenantId) {
    if (user.role === 'SUPER_ADMIN' && user.tenantId === SUPER_ADMIN_SYNTHETIC_TENANT_ID) {
      return undefined;
    }

    return user.tenantId;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  const persistedTenantId = window.localStorage.getItem('fh:last-tenant-id')?.trim();
  if (persistedTenantId) {
    if (persistedTenantId === SUPER_ADMIN_SYNTHETIC_TENANT_ID) {
      window.localStorage.removeItem('fh:last-tenant-id');
      return undefined;
    }

    return persistedTenantId;
  }

  const hostname = window.location.hostname.trim().toLowerCase();
  if (!hostname || hostname === 'localhost' || isIpv4Host(hostname)) {
    return undefined;
  }

  if (hostname.endsWith('.localhost')) {
    const localSubdomain = hostname.replace(/\.localhost$/, '');
    return localSubdomain && !isReservedWorkspaceLabel(localSubdomain)
      ? localSubdomain
      : undefined;
  }

  const parts = hostname.split('.').filter(Boolean);
  if (parts.length >= 3) {
    const candidate = parts[0];
    return !isReservedWorkspaceLabel(candidate) ? candidate : undefined;
  }

  return undefined;
}

// Request interceptor - add auth token + track mutations
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { tokens, user } = useAuthStore.getState();

    // Proactive token refresh — if the token expires within 60 seconds,
    // refresh it BEFORE the request to avoid a 401 round-trip.
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      let exp = 0;
      try {
        const payload = JSON.parse(atob(tokens.accessToken.split('.')[1]));
        exp = payload.exp || 0;
      } catch { /* malformed token — skip proactive refresh */ }
      if (exp && exp - Date.now() / 1000 < 60) {
        try {
          await useAuthStore.getState().refreshToken();
        } catch {
          // Refresh failed — let the request proceed with the old token;
          // the response interceptor will handle the resulting 401.
        }
      }
    }

    // Re-read tokens after potential refresh
    const currentTokens = useAuthStore.getState().tokens;
    if (currentTokens?.accessToken) {
      config.headers.Authorization = `Bearer ${currentTokens.accessToken}`;
    }

    const tenantHeader = resolveTenantHeader(user);
    if (tenantHeader) {
      config.headers['X-Tenant-ID'] = tenantHeader;
    }

    if (isMutation(config)) {
      useApiLoadingStore.getState()._increment();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors and token refresh
let isRefreshing = false;
// Set once a terminal, non-recoverable auth outcome has been handled and a
// full-page redirect to /login is on its way (session replaced on another
// device, token expired, or refresh failed on a protected route). Every other
// in-flight request will also 401 — and an authenticated page fires many at
// once — so without this guard each rejection re-runs the alert + logout +
// redirect. That is exactly what made the "logged in on another device" dialog
// pop back up no matter how many times the user clicked OK. We swallow those
// follow-on errors quietly; the pending navigation reloads the app and resets
// this flag back to false. It is ONLY set on paths that actually navigate, so
// it can never get stuck on a page we intentionally stay on.
let sessionInvalidated = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    if (isMutation(response.config)) {
      useApiLoadingStore.getState()._decrement();
    }
    return response;
  },
  async (error: AxiosError) => {
    reportApiError(error);

    // A terminal logout + redirect is already in flight (see sessionInvalidated
    // note above). Swallow every follow-on error quietly so we don't stack a
    // second alert / logout / redirect for each of the other in-flight requests
    // that 401 at the same time.
    if (sessionInvalidated) {
      if (isMutation(error.config)) {
        useApiLoadingStore.getState()._decrement();
      }
      return Promise.reject(error);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const requestPath = originalRequest?.url;
    const responseData = error.response?.data as { code?: string; message?: string | string[] } | undefined;
    const responseMessage = Array.isArray(responseData?.message)
      ? responseData?.message.join(' ')
      : responseData?.message;
    const isTenantContext403 =
      error.response?.status === 403 &&
      typeof responseMessage === 'string' &&
      responseMessage.includes('Tenant context could not be resolved');

    if (error.response?.status === 401 && responseData?.code === 'SESSION_REPLACED') {
      sessionInvalidated = true;
      // Hand the reason to the login page via sessionStorage (survives the
      // logout + full redirect) so it can show a proper toast, instead of a
      // native blocking window.alert that stacked one dialog per in-flight
      // request.
      try {
        window.sessionStorage.setItem('auth:logoutReason', 'session_replaced');
      } catch {
        // sessionStorage unavailable (private mode / quota) — non-fatal.
      }
      await useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && responseData?.code === 'TOKEN_EXPIRED') {
      sessionInvalidated = true;
      await useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle 401 and tenant-context 403 errors with refresh flow
    if (
      (error.response?.status === 401 || isTenantContext403) &&
      !originalRequest?._retry &&
      !shouldSkipRefresh(requestPath)
    ) {
      if (isRefreshing) {
        // Queue requests while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await useAuthStore.getState().refreshToken();
        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError);
        const currentPath = window.location.pathname;
        useAuthStore.getState().logout();
        // Do not redirect away from public auth pages – those pages are
        // intentionally accessible without a session (forgot-password).
        // Only redirect to /login when the user is on a protected route.
        const publicAuthPaths = ['/login', '/forgot-password'];
        const isPublicPath = publicAuthPaths.some((p) => currentPath.startsWith(p));
        if (!isPublicPath) {
          // Same one-shot guard: a protected-route refresh failure logs out and
          // redirects, so suppress the other concurrent 401s. We do NOT set it
          // on public auth pages, where we stay put and must keep handling
          // their errors normally.
          sessionInvalidated = true;
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Error handling — provide user-friendly messages for common errors
    const status = error.response?.status;
    const data = error.response?.data as Record<string, unknown> | undefined;

    if (status === 409) {
      // Optimistic lock conflict or duplicate — prompt user to retry
      const msg =
        (data?.message as string) ||
        'This record was modified by another user. Please refresh and try again.';
      console.warn('[API] 409 Conflict:', msg);
      // Attach a typed flag so callers can detect conflicts programmatically
      (error as AxiosError & { isConflict: boolean }).isConflict = true;
    }

    if (status === 403) {
      console.warn('[API] 403 Forbidden — insufficient role/permissions');
    }

    if (status === 429) {
      console.warn('[API] 429 Too Many Requests — rate limited');
    }

    if (isMutation(error.config)) {
      useApiLoadingStore.getState()._decrement();
    }

    return Promise.reject(error);
  },
);

// Named export for services that import { apiClient }
export { apiClient };

// Default export
export default apiClient;

// Helper methods
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<T>(url, { params }).then((res) => res.data),

  post: <T>(url: string, data?: unknown) =>
    apiClient.post<T>(url, data).then((res) => res.data),

  put: <T>(url: string, data?: unknown) =>
    apiClient.put<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: unknown) =>
    apiClient.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string) => apiClient.delete<T>(url).then((res) => res.data),

  upload: <T>(url: string, file: File, onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient
      .post<T>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onProgress(progress);
          }
        },
      })
      .then((res) => res.data);
  },
};
